import React, { useEffect, useState, useRef } from "react";
import _OrderRow from "./_OrderRow";
import { FaSearch } from "react-icons/fa";
import { useAppContext } from "../../../../../../Components/AppProvider";

function _PendingPayment() {
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [pendingOrder, setPendingOrder] = useState([]);
  const [updatePendingOrder, setUpdatePendingOrder] = useState(false);
  const { setCountListUpdate } = useAppContext();

  // ✅ search state
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ keep track of selected orders
  const [selectedOrders, setSelectedOrders] = useState([]);

  // ✅ ref for select-all checkbox (to set indeterminate)
  const selectAllRef = useRef();

  useEffect(() => {
    let intervalId;

    async function fetchOrderContent() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/orders/get-for-approval-order`,
          { credentials: "include" }
        );

        if (!res.ok) {
          console.error("HTTP error:", res.status);
          return;
        }

        const data = await res.json();
        if (Array.isArray(data.transactions)) {
          setPendingOrder(data.transactions);
          //console.log("data.pendingOrder :", data.transactions);
        } else {
          console.error("Invalid response:", data);
          setPendingOrder([]);
        }
      } catch (err) {
        console.error("Failed to fetch pending orders", err);
        setPendingOrder([]);
      }
    }

    fetchOrderContent(); // run immediately

    intervalId = setInterval(fetchOrderContent, 10000); // refresh every 10s

    return () => clearInterval(intervalId); // cleanup on unmount
  }, [updatePendingOrder]);

  // 🔹 Approve a single order
  // 🔹 Approve single transaction
  const handleApprove = async (transaction) => {
    const confirm = window.confirm(
      `Are you sure you want to approve payment for transaction ${transaction.TransactionNumber}?`
    );
    if (!confirm) return;

    try {
      // Build payload from items
      const payload = transaction.OrderItems.map((item) => ({
        PT_ID: transaction.TransactionID,
        COI_ID: item.COI_ID,
        status: item.Status,
        payment:
          item.Deposited != 0
            ? item.Balance
            : item.Status === "PartialApproval"
            ? item.Downpayment // or DownPayment field if exists
            : item.UnitPrice, // replace with OriginalPrice if you track it
        balance: item.Balance,
        quantity: item.Quantity,
      }));

      //console.log("Payload for approve:", payload);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/payments/approve-payment/bulk`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ orders: payload }),
        }
      );

      if (res.ok) {
        alert(`✅ Transaction ${transaction.TransactionNumber} approved`);
        setUpdatePendingOrder((prev) => !prev);
        setCountListUpdate((prev) => !prev);
      } else {
        console.error("Failed to approve transaction");
      }
    } catch (err) {
      console.error("Error approving transaction:", err);
    }
  };

  // bulk approve — selected Transactions (selectedOrders contains TransactionID)
  const handleApproveSelected = async () => {
    if (selectedOrders.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to approve ${selectedOrders.length} selected transaction(s)?`
    );
    if (!ok) return;
    //console.log("selectedOrders: ", selectedOrders);
    try {
      const payload = selectedOrders.flatMap((txId) => {
        const transaction = pendingOrder.find((t) => t.TransactionID === txId);
        if (!transaction) {
          console.warn("Transaction with ID not found:", txId);
          return [];
        }
        return transaction.OrderItems.map((item) => ({
          PT_ID: Number(transaction.TransactionID),
          COI_ID: Number(item.COI_ID),
          PD_ID: Number(item.PD_ID),
          status: item.Status,
          payment:
            item.Deposited != 0
              ? item.Balance
              : item.Status === "PartialApproval"
              ? item.Downpayment // or DownPayment field if exists
              : item.UnitPrice,
          quantity: Number(item.Quantity ?? 0),
        }));
      });

      if (payload.length === 0) {
        console.error("No valid transactions found for approval");
        return;
      }

      //console.log("Bulk approve payload:", payload);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/payments/approve-payment/bulk`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ orders: payload }),
        }
      );

      if (res.ok) {
        alert("✅ Selected items have been approved.");
        setUpdatePendingOrder((p) => !p);
        setCountListUpdate((p) => !p);
        setSelectedOrders([]);
      } else {
        console.error(
          "Failed to approve selected transactions",
          await res.text()
        );
      }
    } catch (err) {
      console.error("Error approving selected transactions:", err);
    }
  };
  // 🔹 Decline single transaction
  const handleDecline = async (transaction) => {
    const confirm = window.confirm(
      `Are you sure you want to decline payment for transaction ${transaction.TransactionNumber}?`
    );
    if (!confirm) return;

    try {
      const payload = transaction.OrderItems.map((item) => ({
        COI_ID: item.COI_ID,
        PT_ID: transaction.TransactionID,
      }));

      //console.log("Decline payload:", payload);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/payments/decline-payment/bulk`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ paymentDecline: payload }),
        }
      );

      if (res.ok) {
        alert(`❌ Transaction ${transaction.TransactionNumber} declined`);
        setUpdatePendingOrder((prev) => !prev);
        setCountListUpdate((prev) => !prev);
      } else {
        console.error("Failed to decline transaction");
      }
    } catch (err) {
      console.error("Error declining transaction:", err);
    }
  };

  // 🔹 Decline multiple transactions
  const handleDeclineSelected = async () => {
    if (selectedOrders.length === 0) return;

    const ok = window.confirm(
      `Are you sure you want to decline ${selectedOrders.length} selected transaction(s)?`
    );
    if (!ok) return;

    try {
      const payload = selectedOrders.flatMap((txId) => {
        const transaction = pendingOrder.find((t) => t.TransactionID === txId);
        if (!transaction) {
          console.warn("Transaction with ID not found:", txId);
          return [];
        }
        return transaction.OrderItems.map((item) => ({
          COI_ID: Number(item.COI_ID),
          PT_ID: Number(transaction.TransactionID),
        }));
      });

      if (payload.length === 0) {
        console.error("No valid transactions found for decline");
        return;
      }

      //console.log("Decline selected payload:", payload);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/payments/decline-payment/bulk`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ paymentDecline: payload }),
        }
      );

      if (res.ok) {
        //console.log("Selected transactions declined:", payload);
        alert("❌ Selected transactions declined.");
        setUpdatePendingOrder((prev) => !prev);
        setCountListUpdate((prev) => !prev);
        setSelectedOrders([]); // ✅ clear selection
      } else {
        console.error(
          "Failed to decline selected transactions",
          await res.text()
        );
      }
    } catch (err) {
      console.error("Error declining selected transactions:", err);
    }
  };

  // ✅ filter orders based on search term
  const filteredOrders = pendingOrder.filter((order) => {
    const keyword = searchTerm.toLowerCase();

    const displayStatus =
      order.COI_Status === "PartialApproval"
        ? "For Approval (DP)"
        : order.COI_Status === "FullApproval"
        ? "For Approval (FP)"
        : order.COI_Status || "";

    return (
      order.PD_Name?.toLowerCase().includes(keyword) ||
      order.PO_Number?.toLowerCase().includes(keyword) ||
      `${order.CP_Firstname} ${order.CP_Lastname}`
        .toLowerCase()
        .includes(keyword) ||
      displayStatus.toLowerCase().includes(keyword)
    );
  });

  // ✅ handle toggle of one row
  const handleRowSelect = (txId) => {
    if (!txId) return; // safety check
    setSelectedOrders((prev) =>
      prev.includes(txId) ? prev.filter((id) => id !== txId) : [...prev, txId]
    );
  };
  // ✅ handle toggle all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(filteredOrders.map((o) => o.TransactionID)); // use TransactionID
    } else {
      setSelectedOrders([]);
    }
  };

  // ✅ check if all are selected
  const allSelected =
    filteredOrders.length > 0 &&
    filteredOrders.every((o) => selectedOrders.includes(o.TransactionID));

  // ✅ set indeterminate state
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedOrders.length > 0 && !allSelected;
    }
  }, [selectedOrders, allSelected]);

  return (
    <main className="ad-order-panel-container">
      <div className="order-panel-header">
        <div className="order-panel-title-wrapper">
          <span>APPROVAL OF PAYMENT</span>
        </div>
        <div className="order-panel-actions-wrapper">
          {selectedOrders.length > 0 && (
            <>
              <button onClick={handleApproveSelected}>
                APPROVE ({selectedOrders.length})
              </button>
              <button onClick={handleDeclineSelected}>
                DECLINE ({selectedOrders.length})
              </button>
            </>
          )}

          <div className="order-search-bar">
            <input
              type="text"
              placeholder="Keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span>
              <FaSearch />
            </span>
          </div>
        </div>
      </div>

      <div className="order-panel-table-wrapper">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>
                <input
                  type="checkbox"
                  ref={selectAllRef}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th>TRANSACTION NUMBER</th>
              <th>CUSTOMER NAME</th>
              <th>NO. OF ITEMS</th>
              <th>DATE ORDERED</th>
              <th>PAYMENT METHOD</th>
              <th>PAYMENT TYPE</th>
              <th>AMOUNT PAID</th>
              <th>STATUS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((transaction) => (
                <_OrderRow
                  key={transaction.TransactionID}
                  transaction={transaction}
                  menuOpenId={menuOpenId}
                  setMenuOpenId={setMenuOpenId}
                  isSelected={selectedOrders.includes(
                    transaction.TransactionID
                  )}
                  onSelect={handleRowSelect} // <-- not wrapped
                  handleApprove={handleApprove}
                  handleDecline={handleDecline}
                />
              ))
            ) : (
              <tr>
                <td
                  colSpan="11"
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

export default _PendingPayment;
